import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Quotes, ArrowRight, GraduationCap, Check } from "@phosphor-icons/react";
import { Reveal, Eyebrow, MaskLines } from "../components/Motion";
import { BUSINESS, BIO, CREDENTIALS, VALUES } from "../constants/site";

export default function About() {
  return (
    <div className="pt-36 sm:pt-44">
      {/* Intro + portrait */}
      <section className="max-w-[1440px] mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.25fr_0.9fr] gap-14 lg:gap-20 items-start">
        <div>
          <Eyebrow>{BIO.headline}</Eyebrow>
          <h1 className="font-display font-light leading-[0.95] text-[clamp(2.6rem,7vw,6.5rem)] text-[color:var(--text)] mt-5">
            <MaskLines lines={["Leticia Dean", "Capo Marquez"]} delay={0.15} />
          </h1>
          <Reveal delay={0.3}>
            <div className="space-y-5 mt-8 max-w-xl">
              {BIO.paragraphs.slice(0, 2).map((p, i) => (
                <p key={i} className="text-[color:var(--text-2)] text-base sm:text-lg leading-relaxed">{p}</p>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <div className="relative">
            <div className="absolute -inset-4 radial-spot" />
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              whileInView={{ clipPath: "inset(0% 0 0 0)" }}
              viewport={{ once: true }}
              transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
              className="relative overflow-hidden arch border border-[color:var(--gold)]/30 gold-glow"
            >
              <img src={BUSINESS.founderPhoto} alt={BUSINESS.founder} className="w-full object-cover" data-testid="founder-image" />
            </motion.div>
            <div className="mt-6 text-center">
              <p className="font-display text-2xl text-[color:var(--text)]">{BUSINESS.founder}</p>
              <p className="font-mono-x text-[10px] tracking-[0.3em] uppercase text-[color:var(--gold)] mt-1">Founder · {BUSINESS.location}</p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Remaining biography */}
      <section className="max-w-[1440px] mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="grid lg:grid-cols-[0.35fr_1fr] gap-10 lg:gap-16 border-t hairline pt-12">
          <Reveal>
            <Eyebrow>Her story</Eyebrow>
          </Reveal>
          <div className="space-y-6 max-w-3xl">
            {BIO.paragraphs.slice(2).map((p, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <p className="text-[color:var(--text-2)] text-lg leading-relaxed">{p}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="max-w-[1440px] mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <Reveal>
          <Quotes size={56} weight="fill" className="text-[color:var(--gold)] opacity-70 mx-auto" />
          <p className="font-display font-light italic text-[clamp(1.8rem,4.5vw,4rem)] leading-[1.15] text-center max-w-5xl mx-auto mt-8 text-[color:var(--text)]">
            "Every client arrives carrying stress. My work is to hand it back to them as
            <span className="text-gradient-gold not-italic"> success</span>."
          </p>
          <p className="text-center font-mono-x text-xs tracking-[0.3em] uppercase text-[color:var(--muted)] mt-8">
            — {BUSINESS.founder}
          </p>
        </Reveal>
      </section>

      {/* Education & Values */}
      <section className="border-y hairline bg-[color:var(--surface)]">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 py-24 grid lg:grid-cols-2 gap-16">
          <Reveal>
            <div className="flex items-center gap-4">
              <GraduationCap size={26} className="text-[color:var(--gold)]" weight="light" />
              <Eyebrow>Education & Credentials</Eyebrow>
            </div>
            <ul className="mt-8 space-y-1">
              {CREDENTIALS.map((c) => (
                <li key={c} className="flex items-start gap-4 py-4 border-b hairline">
                  <Check size={16} className="text-[color:var(--gold)] shrink-0 mt-1.5" weight="bold" />
                  <span className="text-[color:var(--text)] text-lg">{c}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.1}>
            <Eyebrow>Values We Live By</Eyebrow>
            <div className="mt-8 flex flex-wrap gap-3">
              {VALUES.map((v) => (
                <span key={v} className="font-display text-xl sm:text-2xl border border-[color:var(--gold)]/25 rounded-full px-6 py-3 text-[color:var(--text)] hover:border-[color:var(--gold)] hover:text-[color:var(--gold)] transition-colors duration-500">
                  {v}
                </span>
              ))}
            </div>
            <p className="text-[color:var(--text-2)] mt-10 leading-relaxed max-w-md">
              Discreet, dependable and endlessly creative — from the first conversation to the final
              print-ready file, your project is treated as our own. That is the {BUSINESS.short} way.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-5 sm:px-8 py-24 text-center">
        <Reveal>
          <Link
            to="/contact"
            data-testid="about-cta"
            className="group inline-flex items-center gap-3 rounded-full border border-[color:var(--gold)] px-10 py-4 text-[color:var(--gold)] transition-all duration-500 hover:bg-[color:var(--gold)] hover:text-[#0b0710]"
          >
            Work with us
            <ArrowRight className="transition-transform duration-500 group-hover:translate-x-1" size={18} weight="bold" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
